import type { PayloadAction } from "@reduxjs/toolkit";

import { createAppSlice } from "../app/createAppSlice";
import { Role } from "../config/constants";

// Use the enum for hierarchy
const ROLE_HIERARCHY: Role[] = [
  Role.Reader,
  Role.Contributer,
  Role.Editor,
  Role.Owner,
  Role.Creator,
  Role.SecurityManager,
];

// Now type the argument as Role
export const getRoleRank = (role: Role) => ROLE_HIERARCHY.indexOf(role);

export const hasReadAccess = (roles: string[]): boolean => {
  // Read access: any role except SecurityManager
  return roles.some(role => {
    const r = role as Role;
    return (
      r === Role.Reader ||
      r === Role.Contributer ||
      r === Role.Editor ||
      r === Role.Owner ||
      r === Role.Creator ||
      r === Role.SecurityManager
    );
  });
};

export const hasWriteAccess = (roles: string[]): boolean => {
  // Write access: Editor or higher (Editor, Owner, Creator)
  return roles.some(role => {
    const r = role as Role;
    return r === Role.Editor || r === Role.Owner || r === Role.Creator;
  });
};

export interface RoleEntry {
  Role: string;
  Principal: {
    PrincipalType: string;
    PrincipalId: string;
  };
  InheritedFrom: string;
}

interface PermissionsState {
  pageRoles: Record<string, string[]>; // { "timetransform": ["Owner", "Reader"] }
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: PermissionsState = {
  pageRoles: {},
  status: "idle",
  error: null,
};

export const permissionsSlice = createAppSlice({
  name: "permissions",
  initialState,
  reducers: {
    setPageRoles: (state, action: PayloadAction<{ path: string; roles: string[] }>) => {
      const normalizedPath = action.payload.path.replace(/^\/|\/$/g, ""); // Remove leading and trailing slashes
      state.pageRoles[normalizedPath] = action.payload.roles;
    },
    setPermissionsStatus: (state, action: PayloadAction<PermissionsState["status"]>) => {
      state.status = action.payload;
    },
    setPermissionsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearPermissions: state => {
      state.pageRoles = {};
      state.status = "idle";
      state.error = null;
    },
  },
  selectors: {
    getRolesForPath: state => (path: string) => {
      const normalizedPath = path.replace(/^\/|\/$/g, ""); // Normalize the path
      return state.pageRoles[normalizedPath] || [];
    },
    getPermissionsStatus: state => state.status,
    getPermissionsError: state => state.error,
  },
});

export const { setPageRoles, setPermissionsStatus, setPermissionsError, clearPermissions } = permissionsSlice.actions;

export const { getRolesForPath, getPermissionsStatus, getPermissionsError } = permissionsSlice.selectors;
